/*
 * Copyright (c) 2024 InvestNYou. All rights reserved.
 * 
 * This software is protected by copyright law. Unauthorized copying,
 * reproduction, or distribution of this software, in whole or in part,
 * without the express written permission of InvestNYou, is strictly
 * prohibited.
 */

import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import './App.css';
import './tailwind-utilities.css';
import './styles/desktop.css';

// Components
import WelcomeScreen from './components/WelcomeScreen';
import Login from './components/Login';
import SignUp from './components/SignUp';
import Onboarding from './components/Onboarding';
import Dashboard from './components/Dashboard';
import Learning from './components/Learning';
import Course from './components/Course';
import Lesson from './components/Lesson';
import Quiz from './components/Quiz';
import AskFeature from './components/AskFeature';
// Portfolio is now accessed through the Simulations page
import Challenges from './components/Challenges';
import ChallengeDetails from './components/ChallengeDetails';
import Watchlist from './components/Watchlist';
import Forum from './components/Forum';
import StudentBlockedRoute from './components/StudentBlockedRoute';
import Leaderboard from './components/Leaderboard';
import TermsOfService from './components/TermsOfService';
import PrivacyPolicy from './components/PrivacyPolicy';
import PatchNotes from './components/PatchNotes';
import Help from './components/Help';
import Profile from './components/Profile';
import NotFound from './components/NotFound';
import ProtectedRoute from './components/ProtectedRoute';
import AdminPanel from './components/AdminPanel';
import LeagueManagement from './components/LeagueManagement';
import ClassAssignments from './components/ClassAssignments';
import Simulations from './components/Simulations';
import AuthGuard from './components/AuthGuard';
import AuthChecker from './components/AuthChecker';
import { UserProvider } from './context/UserContext';
import { ThemeProvider } from './context/ThemeContext';
import AuthService from './services/AuthService';

function App() {
  // Support basename for subdirectory deployments (e.g., /app)
  // Set REACT_APP_BASENAME in environment variables if needed
  const basename = process.env.REACT_APP_BASENAME || '';

  return (
    <ThemeProvider>
      <UserProvider>
      <AuthChecker>
        <Router basename={basename}>
          <div className="App">
            <Toaster 
              position="top-center"
              toastOptions={{
                duration: 3000,
                style: {
                  background: '#363636',
                  color: '#fff',
                },
              }}
            />
            <Routes>
            <Route path="/" element={<WelcomeScreen />} />
            <Route path="/login" element={
              <AuthGuard>
                <Login />
              </AuthGuard>
            } />
            <Route path="/signup" element={
              <AuthGuard>
                <SignUp />
              </AuthGuard>
            } />
            <Route path="/onboarding" element={<Onboarding />} />
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            } />
            <Route path="/learn" element={
              <ProtectedRoute>
                <Learning />
              </ProtectedRoute>
            } />
            <Route path="/course/:courseId" element={
              <ProtectedRoute>
                <Course />
              </ProtectedRoute>
            } />
            <Route path="/course/:courseId/lesson/:lessonId" element={
              <ProtectedRoute>
                <Lesson />
              </ProtectedRoute>
            } />
            <Route path="/course/:courseId/lesson/:lessonId/quiz/:quizId" element={
              <ProtectedRoute>
                <Quiz />
              </ProtectedRoute>
            } />
            <Route path="/teacher-lesson/:lessonId" element={
              <ProtectedRoute>
                <Lesson />
              </ProtectedRoute>
            } />
            <Route path="/teacher-quiz/:quizId" element={
              <ProtectedRoute>
                <Quiz />
              </ProtectedRoute>
            } />
            <Route path="/ask" element={
              <ProtectedRoute>
                <AskFeature />
              </ProtectedRoute>
            } />
            <Route path="/portfolio" element={
              <Navigate to="/simulations?tab=portfolio" replace />
            } />
            <Route path="/simulations" element={
              <ProtectedRoute>
                <Simulations />
              </ProtectedRoute>
            } />
            <Route path="/class" element={
              <ProtectedRoute>
                <ClassAssignments />
              </ProtectedRoute>
            } />
            <Route path="/challenges" element={
              <ProtectedRoute>
                <Challenges />
              </ProtectedRoute>
            } />
            <Route path="/challenges/league/:leagueId/:challengeId" element={
              <ProtectedRoute>
                <ChallengeDetails />
              </ProtectedRoute>
            } />
            <Route path="/challenges/:challengeId" element={
              <ProtectedRoute>
                <ChallengeDetails />
              </ProtectedRoute>
            } />
            <Route path="/watchlist" element={
              <ProtectedRoute>
                <Watchlist />
              </ProtectedRoute>
            } />
            <Route path="/forum" element={
              <ProtectedRoute>
                <StudentBlockedRoute>
                  <Forum />
                </StudentBlockedRoute>
              </ProtectedRoute>
            } />
            <Route path="/forum/:postId" element={
              <ProtectedRoute>
                <StudentBlockedRoute>
                  <Forum />
                </StudentBlockedRoute>
              </ProtectedRoute>
            } />
            <Route path="/leaderboard" element={
              <ProtectedRoute>
                <Leaderboard />
              </ProtectedRoute>
            } />
            <Route path="/admin" element={
              <ProtectedRoute>
                <AdminPanel />
              </ProtectedRoute>
            } />
            <Route path="/leagues" element={
              <ProtectedRoute>
                <LeagueManagement />
              </ProtectedRoute>
            } />
            <Route path="/profile" element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            } />
            <Route path="/help" element={
              <ProtectedRoute>
                <Help />
              </ProtectedRoute>
            } />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/patchnotes" element={<PatchNotes />} />
            {/* Catch-all route for client-side routing */}
            <Route path="*" element={<NotFound />} />
            </Routes>
          </div>
        </Router>
      </AuthChecker>
      </UserProvider>
    </ThemeProvider>
  );
}

export default App;
